import { useForm, useFieldArray } from "react-hook-form";
import LocalizedInput from "../common/LocalizedInput";
import resolver from "@congrs/resolver";
import {
  makeI18nDefaultValue,
  mergeI18nWithDefaults,
} from "../../utils/i18nUtils";
import { useEffect, useRef } from "react";
import { Container, Paper, Title, Button, Group, Text } from "@mantine/core";
import { Select, TextInput, Textarea } from "react-hook-form-mantine";
import { extractDirtyData } from "../../utils/dirtyData";
import {
  web_createSurveyQuestionsSchema,
  web_patchSurveyQuestionSchema,
} from "@congrs/schemas";

function QuestionForm({
  event,
  question,
  onSubmit: onSubmitCallback,
  type = "create",
}) {
  const isEdit = type === "edit";

  const mainLanguage = event?.language || "en";
  const otherLanguages = event?.secondaryLanguages;

  const buildAnswerOptions = (questionData) => {
    if (questionData.questionType === "text") return null;
    if (questionData.answerOptions?.length > 0) {
      return questionData.answerOptions.map((opt) => ({
        labelI18n: mergeI18nWithDefaults(
          opt.labelI18n,
          mainLanguage,
          otherLanguages
        ),
      }));
    }
    return [{ labelI18n: makeI18nDefaultValue(mainLanguage, otherLanguages) }];
  };

  const getDefaultValues = () => {
    if (isEdit && question) {
      return {
        questionTextI18n: mergeI18nWithDefaults(
          question.questionTextI18n,
          mainLanguage,
          otherLanguages
        ),
        questionType: question.questionType || "text",
        answerOptions: buildAnswerOptions(question),
      };
    }
    return {
      questionTextI18n: makeI18nDefaultValue(mainLanguage, otherLanguages),
      questionType: null,
      answerOptions: null,
    };
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isSubmitting, isDirty, dirtyFields },
  } = useForm({
    defaultValues: getDefaultValues(),
    resolver: resolver(
      isEdit ? web_patchSurveyQuestionSchema : web_createSurveyQuestionsSchema
    ),
  });

  const questionType = watch("questionType");
  const prevQuestionType = useRef(questionType);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "answerOptions",
  });

  useEffect(() => {
    if (isEdit) {
      if (prevQuestionType.current === questionType) return;
    } else {
      if (questionType === null || prevQuestionType.current === questionType)
        return;
    }

    prevQuestionType.current = questionType;

    if (questionType === "text") {
      setValue("answerOptions", null);
    } else {
      const opts = watch("answerOptions");
      if (!opts || opts.length === 0) {
        setValue("answerOptions", [
          { labelI18n: makeI18nDefaultValue(mainLanguage, otherLanguages) },
        ]);
      }
    }
  }, [questionType, setValue, watch, mainLanguage, otherLanguages, isEdit]);

  const onSubmit = (data) => {
    let payload = data;

    if (isEdit) {
      payload = extractDirtyData(data, dirtyFields);

      if (data.questionTextI18n) {
        payload.questionTextI18n = data.questionTextI18n;
      }

      if (data.questionType === "text") {
        payload.answerOptions = null;
      }

      if (data.questionType !== "text" && data.answerOptions) {
        payload.answerOptions = data.answerOptions.map((opt) => ({
          labelI18n: opt.labelI18n,
        }));
      }
    }

    onSubmitCallback(payload, { reset, data, getDefaultValues });
  };

  const shouldShowOptions = () => {
    if (questionType === "text") return false;
    if (isEdit) return fields.length > 0;
    return questionType !== null && fields.length > 0;
  };

  return (
    <Container size="sm" my="md">
      <Paper withBorder p="md">
        <Title order={3} mb="md">
          {isEdit ? "Edit Survey Question" : "Create Survey Question"}
        </Title>

        <form onSubmit={handleSubmit(onSubmit)}>
          <LocalizedInput
            control={control}
            fields={[
              {
                nameBase: "questionTextI18n",
                label: "Question Text",
                component: Textarea,
                props: {
                  autosize: true,
                  minRows: 3,
                  size: "sm",
                  withAsterisk: true,
                },
              },
            ]}
            mainLanguage={mainLanguage}
            otherLanguages={otherLanguages}
          />

          <Select
            label="Question Type"
            name="questionType"
            control={control}
            data={[
              { value: "multiple_choice", label: "Multiple Choice" },
              { value: "single_choice", label: "Single Choice" },
              { value: "text", label: "Text" },
            ]}
            withAsterisk
            mt="md"
            allowDeselect={false}
          />

          {shouldShowOptions() && (
            <>
              <Text fw={500} mt="md" mb="xs">
                Options
              </Text>

              {fields.map((field, index) => (
                <Paper key={field.id} p="sm" mt="sm" withBorder>
                  <Group gap="sm" style={{ alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                      <LocalizedInput
                        control={control}
                        fields={[
                          {
                            nameBase: `answerOptions.${index}.labelI18n`,
                            label: "Label",
                            component: TextInput,
                            props: { withAsterisk: true },
                          },
                        ]}
                        mainLanguage={mainLanguage}
                        otherLanguages={otherLanguages}
                      />

                      <Button
                        color="red"
                        variant="light"
                        mt="sm"
                        style={{ alignSelf: "flex-start" }}
                        onClick={() => remove(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  </Group>
                </Paper>
              ))}

              <Button
                mt="md"
                variant="light"
                onClick={() =>
                  append({
                    labelI18n: makeI18nDefaultValue(
                      mainLanguage,
                      otherLanguages
                    ),
                  })
                }
              >
                Add Option
              </Button>
            </>
          )}

          <Button
            type="submit"
            mt="xl"
            fullWidth
            disabled={!isDirty || isSubmitting}
          >
            {isEdit ? "Update Question" : "Create Question"}
          </Button>
        </form>
      </Paper>
    </Container>
  );
}

export default QuestionForm;
